import { useState, useMemo } from "react";
import { Check, Plus, Trash2 } from "lucide-react";
import { useAdminData } from "../../hooks/useAdminData";
import { announcements as announcementsApi } from "../../api/endpoints";
import { AddToast } from "../../components/feedback/Toast";
import { Btn } from "../../components/ui/Btn";
import { Card } from "../../components/ui/Card";
import { FilterChips } from "../../components/ui/FilterChips";
import { PageHeader } from "../../components/ui/PageHeader";
import { StatusBadge } from "../../components/ui/StatusBadge";
import { F, T } from "../../theme/tokens";

// ── Communications ───────────────────────────────────────────────────────────

export function CommunicationsView({ addToast }: { addToast: AddToast }) {
  const { ADMIN_ANNOUNCEMENTS, refetchAnnouncements } = useAdminData();
  const [composing, setComposing] = useState(false);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [audience, setAudience] = useState("All");
  const [statusFilter, setStatusFilter] = useState("All");

  const filtered = useMemo(() =>
    ADMIN_ANNOUNCEMENTS.filter((a) => a.status !== "Scheduled" && (statusFilter === "All" || a.status === statusFilter)),
    [ADMIN_ANNOUNCEMENTS, statusFilter]
  );

  function sendNow() {
    announcementsApi.create({ title, body, audience, status: "Sent" })
      .then(() => { refetchAnnouncements(); addToast("Announcement sent", "success"); setComposing(false); setTitle(""); setBody(""); })
      .catch(() => addToast("Failed to send announcement", "error"));
  }
  function saveDraft() {
    announcementsApi.create({ title, body, audience, status: "Draft" })
      .then(() => { refetchAnnouncements(); addToast("Saved as draft", "info"); setComposing(false); setTitle(""); setBody(""); })
      .catch(() => addToast("Failed to save draft", "error"));
  }
  function sendDraft(id: string) {
    announcementsApi.update(id, { status: "Sent" })
      .then(() => { refetchAnnouncements(); addToast("Announcement sent", "success"); })
      .catch(() => addToast("Failed to send announcement", "error"));
  }
  function deleteAnnouncement(id: string) {
    announcementsApi.remove(id)
      .then(() => { refetchAnnouncements(); addToast("Deleted", "error"); })
      .catch(() => addToast("Failed to delete announcement", "error"));
  }

  return (
    <div>
      <PageHeader title="Communications" sub="Announcements and platform notifications" actions={
        <Btn variant="primary" onClick={() => setComposing(true)} iconOnlyPhone><Plus size={13} /> New announcement</Btn>
      } />

      {composing && (
        <Card style={{ padding: "22px", marginBottom: "22px" }}>
          <p style={{ fontSize: "13px", fontWeight: 600, color: T.text, fontFamily: F, marginBottom: "16px" }}>Compose announcement</p>
          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Subject / Title…" style={{ border: `1px solid ${T.border}`, borderRadius: "8px", padding: "9px 12px", fontSize: "13px", fontFamily: F, outline: "none", color: T.text }} />
            <textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder="Message body…" rows={5} style={{ border: `1px solid ${T.border}`, borderRadius: "8px", padding: "9px 12px", fontSize: "12px", fontFamily: F, outline: "none", color: T.text, resize: "vertical" }} />
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
              <span style={{ fontSize: "12px", color: T.sub, fontFamily: F }}>Audience:</span>
              {["All", "Talents", "Organisations"].map((a) => (
                <button key={a} onClick={() => setAudience(a)} style={{ padding: "5px 12px", borderRadius: "6px", fontSize: "11px", fontFamily: F, fontWeight: 500, cursor: "pointer", border: `1px solid ${audience === a ? T.text : T.border}`, background: audience === a ? T.text : T.surface, color: audience === a ? "#fff" : T.sub, transition: "all 0.15s" }}>
                  {a}
                </button>
              ))}
            </div>
            <div style={{ display: "flex", gap: "8px" }}>
              <Btn variant="primary" onClick={sendNow}><Check size={13} /> Send now</Btn>
              <Btn variant="ghost" onClick={saveDraft}>Save draft</Btn>
              <Btn variant="ghost" onClick={() => setComposing(false)}>Cancel</Btn>
            </div>
          </div>
        </Card>
      )}

      <div style={{ display: "flex", gap: "8px", marginBottom: "16px" }}>
        <FilterChips options={["All", "Sent", "Draft"]} active={statusFilter} onChange={setStatusFilter} />
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
        {filtered.map((a) => (
          <Card key={a.id} style={{ padding: "16px 20px" }}>
            <div className="ann-row" style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "16px" }}>
              <div style={{ flex: 1 }}>
                <div className="ann-title" style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px" }}>
                  <p style={{ fontSize: "13px", fontWeight: 600, color: T.text, fontFamily: F }}>{a.title}</p>
                  <StatusBadge status={a.status} />
                </div>
                <p style={{ fontSize: "11px", color: T.sub, fontFamily: F, marginBottom: "6px" }}>
                  Audience: {a.audience} · By {a.createdBy} · {a.sentDate ?? a.createdDate}
                  {a.recipients > 0 && ` · ${a.recipients.toLocaleString()} recipients`}
                </p>
                <p style={{ fontSize: "12px", color: T.sub, fontFamily: F, lineHeight: 1.6 }}>{a.body}</p>
              </div>
              {a.status === "Draft" && (
                <div className="ann-actions" style={{ display: "flex", gap: "6px" }}>
                  <Btn variant="primary" size="xs" onClick={() => sendDraft(a.id)}>Send</Btn>
                  <Btn variant="ghost" size="xs" onClick={() => deleteAnnouncement(a.id)}><Trash2 size={10} /></Btn>
                </div>
              )}
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
